import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ScreenHeader, Surface, Button, IconButton, Divider, Icon, FeedbackMsg } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

function formatDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function AddModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ data: '', hora_inicio: '', duracao: 60, tipo: 'presencial' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    axios.post(`${API_URL}/horarios`, form, { withCredentials: true })
      .then(() => { onSaved(); onClose(); })
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao criar horário.'))
      .finally(() => setSaving(false));
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: '100%', maxWidth: 480, background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
        boxShadow: 'var(--shadow-lg)', animation: 'fadeSlideUp .25s cubic-bezier(.22,1,.36,1)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--gap-3)', borderBottom: '1px solid var(--border-hairline)',
        }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Novo horário</div>
          <IconButton icon="close" onClick={onClose} variant="ghost"/>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 'var(--gap-3)', display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
          {[
            { name: 'data', label: 'Data', type: 'date', required: true },
            { name: 'hora_inicio', label: 'Horário de início', type: 'time', required: true },
            { name: 'duracao', label: 'Duração (minutos)', type: 'number', min: 15, required: true },
          ].map(f => (
            <div key={f.name} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>{f.label}</label>
              <input
                name={f.name} type={f.type} required={f.required} min={f.min}
                value={form[f.name]} onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))}
                style={{ height: 40, padding: '0 12px', fontSize: 14, background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none' }}
              />
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>Tipo</label>
            <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))}
              style={{ height: 40, padding: '0 12px', fontSize: 14, background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none' }}>
              <option value="presencial">Presencial</option>
              <option value="online">Online</option>
            </select>
          </div>
          {msg && <FeedbackMsg msg={msg}/>}
          <Button variant="primary" size="lg" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
            {saving ? 'Salvando…' : 'Criar horário'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function HorariosDisponiveis() {
  const { tipo } = useAuth();
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  function carregar() {
    setLoading(true);
    axios.get(`${API_URL}/horarios_disponiveis`, { withCredentials: true })
      .then(r => setHorarios(r.data))
      .catch(() => setMsg('Erro ao carregar horários.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { carregar(); }, []);

  function excluir(id) {
    if (!window.confirm('Excluir este horário?')) return;
    axios.delete(`${API_URL}/horarios/${id}`, { withCredentials: true })
      .then(() => { setHorarios(h => h.filter(x => x.id !== id)); setMsg('Horário excluído!'); setTimeout(() => setMsg(''), 3000); })
      .catch(() => setMsg('Erro ao excluir.'));
  }

  function solicitar(id) {
    axios.post(`${API_URL}/agendar/${id}`, {}, { withCredentials: true })
      .then(() => { setMsg('Aula solicitada! Aguarde aprovação.'); setTimeout(() => setMsg(''), 4000); carregar(); })
      .catch(err => { setMsg(err.response?.data?.erro || 'Erro ao solicitar.'); });
  }

  return (
    <div className="mema-screen" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90 }}>
      <ScreenHeader title="Horários abertos" action={
        tipo === 'professora'
          ? <IconButton icon="plus" variant="accent" size={36} onClick={() => setAddOpen(true)} label="Novo horário"/>
          : null
      }/>

      <div style={{ padding: '0 var(--gap-3)' }}>
        {msg && <div style={{ marginBottom: 'var(--gap-2)' }}><FeedbackMsg msg={msg}/></div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--gap-4)', color: 'var(--text-tertiary)', fontSize: 13 }}>Carregando…</div>
        ) : horarios.length === 0 ? (
          <Surface padded style={{ textAlign: 'center', padding: 'var(--gap-4)' }}>
            <Icon name="clock" size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.35, color: 'var(--text-tertiary)' }}/>
            <div style={{ fontSize: 14, fontWeight: 540, color: 'var(--text-primary)' }}>Nenhum horário disponível</div>
            {tipo === 'professora' && (
              <Button size="sm" variant="secondary" icon="plus" style={{ marginTop: 12 }} onClick={() => setAddOpen(true)}>
                Adicionar horário
              </Button>
            )}
          </Surface>
        ) : (
          <Surface>
            {horarios.map((h, i) => (
              <React.Fragment key={h.id}>
                {i > 0 && <Divider/>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', padding: 'var(--pad-card)' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 'var(--radius-md)', flexShrink: 0,
                    background: 'var(--accent-soft)', color: 'var(--accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon name="calendar" size={18}/>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 540, color: 'var(--text-primary)' }}>
                      {formatDate(h.data)} · <span style={{ fontFamily: '"Geist Mono", monospace' }}>{h.hora_inicio}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      <Icon name={h.tipo === 'online' ? 'cam' : 'location'} size={11}/>
                      {h.tipo} · {h.duracao} min
                    </div>
                  </div>
                  {tipo === 'professora'
                    ? <IconButton icon="close" size={28} variant="ghost" style={{ color: 'var(--danger)' }} onClick={() => excluir(h.id)} label="Excluir"/>
                    : <Button size="sm" variant="soft" onClick={() => solicitar(h.id)}>Solicitar</Button>
                  }
                </div>
              </React.Fragment>
            ))}
          </Surface>
        )}
      </div>

      {addOpen && <AddModal onClose={() => setAddOpen(false)} onSaved={carregar}/>}
    </div>
  );
}
